import React, { JSX, useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import {
  PlusIcon,
  ChartBarIcon,
  TrashIcon,
  PencilSquareIcon,
  EyeIcon,
  DocumentDuplicateIcon,
  ArrowPathIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline'
import { LoadingSpinner } from 'components/Loading'
import { NumberInput } from 'components/NumberInput'
import { ColorInput, BackgroundInput } from 'components/ColorPickers'
import { ErrorInline } from 'components/ErrorInline'
import { SavedNote } from 'components/SavedNote'
import { ProviderBrandIcon, TWITCH_BRAND_ICON_SRC } from 'components/ProviderBrandIcon'
import { BackgroundImageInput } from 'components/BackgroundImageInput'
import { ScaledStage } from 'components/ScaledStage'
import { TextSizeInput } from 'components/TextSizeInput'
import { TWITCH_CHANNELS_OPTIONAL_HINT, eventHasYouTubeSource } from 'components/EventYouTubeSources'
import {
  PollDraft,
  PollPreview,
  draftFromPoll,
  pollLiveFromSummary,
  pollTextSizeDefaults,
  sampleClosedPollLive
} from 'components/PollPreview'
import { POLL } from 'lib/elementCanvas'
import {
  TEXT_SIZE_MAX_PX,
  TEXT_SIZE_MIN_PX,
  TICKER_HEIGHT_MAX_PX,
  TICKER_HEIGHT_MIN_PX,
  usePreviewFit
} from 'lib/textSizes'
import { appendTickerOptionWeight, removeTickerOptionWeight } from 'lib/tickerBlocks'
import { elementDisplayName, sortByName } from 'lib/elementOrder'
import { ChatFontKey } from 'lib/chatFonts'
import { FontSelect } from 'components/FontSelect'
import { useAuth } from 'context/user'
import { useEntitlements } from 'context/plan'
import { useEventDetail } from 'hooks/useEventDetail'
import { graphqlRequest } from 'lib/helpers'
import { sumCounts } from 'lib/votePercent'
import {
  PollSummary,
  PollOptionInput,
  PollStatus,
  EventDetail,
  EventOverlay,
  ChannelBinding,
  DeletionResult
} from 'types/api'
import createPollMutation from 'graphql/createPoll.graphql'
import updatePollMutation from 'graphql/updatePoll.graphql'
import updatePollClassicMutation from 'graphql/updatePollClassic.graphql'
import deletePollMutation from 'graphql/deletePoll.graphql'
import duplicatePollMutation from 'graphql/duplicatePoll.graphql'
import getOverlayQuery from 'graphql/getOverlay.graphql'

const DEFAULT_HEADING_TEXT_COLOR = '#FFFFFF'
const DEFAULT_HEADING_BG = 'linear-gradient(135deg, #1E3A8A 0%, #4338CA 100%)'
const DEFAULT_OPTIONS_TEXT_COLOR = '#FFFFFF'
const DEFAULT_OPTIONS_BG = 'linear-gradient(180deg, #2563EB 0%, #1D4ED8 100%)'
const DEFAULT_CTA_TEXT_COLOR = '#FFFFFF'
const DEFAULT_CTA_BG = 'linear-gradient(180deg, #1E1B4B 0%, #0F172A 100%)'
const DEFAULT_CTA_TEXT = 'Type #A, #B, #C, or #D in chat to vote'
const DEFAULT_OPTION_TEXT_COLOR = '#FFFFFF'
const DEFAULT_OPTION_PROGRESS_COLOR = '#67E8F9'
const DEFAULT_CORNER_RADIUS = 16
const MIN_CORNER_RADIUS = 0
const MAX_CORNER_RADIUS = 48
const DEFAULT_BORDER_WIDTH = 0
const DEFAULT_BORDER_COLOR = '#00000000'
const MIN_BORDER_WIDTH = 0
const MAX_BORDER_WIDTH = 24
const DEFAULT_WINNER_OUTLINE_COLOR = '#67E8F9'

const DEFAULT_OPTIONS: Array<{ displayText: string, command: string }> = [
  { displayText: 'Option A', command: '#A' },
  { displayText: 'Option B', command: '#B' }
]

type PollSizeKey =
  | 'questionFontSize'
  | 'tickerLabelFontSize'
  | 'displayTextFontSize'
  | 'commandFontSize'
  | 'numberFontSize'
  | 'callToActionFontSize'
  | 'closedTextFontSize'
  | 'tickerHeight'
const POLL_SIZE_KEYS: PollSizeKey[] = [
  'questionFontSize',
  'tickerLabelFontSize',
  'displayTextFontSize',
  'commandFontSize',
  'numberFontSize',
  'callToActionFontSize',
  'closedTextFontSize',
  'tickerHeight'
]

const STATUS_PILL: Record<PollStatus, string> = {
  ACTIVE: 'bg-emerald-500/15 text-emerald-400 ring-emerald-500/30',
  INACTIVE: 'bg-zinc-500/15 text-zinc-300 ring-zinc-500/30'
}

const STATUS_LABEL: Record<PollStatus, string> = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive'
}

const SectionCard = ({
  title,
  children,
  action
}: { title: string, children: React.ReactNode, action?: React.ReactNode }): JSX.Element => (
  <section className='rounded-xl border border-white/5 bg-dm-bg-secondary p-5'>
    <div className='mb-4 flex items-center justify-between'>
      <h3 className='text-base font-semibold text-dm-text-primary'>{title}</h3>
      {action}
    </div>
    {children}
  </section>
)

const FieldLabel = ({ children }: { children: React.ReactNode }): JSX.Element => (
  <span className='mb-1.5 block text-xs font-medium uppercase tracking-wider text-dm-text-accent'>
    {children}
  </span>
)

const emptyDraft = (): PollDraft => ({
  name: '',
  question: '',
  callToActionText: DEFAULT_CTA_TEXT,
  options: DEFAULT_OPTIONS.map((o) => ({
    ...o,
    textColor: DEFAULT_OPTION_TEXT_COLOR,
    progressBarColor: DEFAULT_OPTION_PROGRESS_COLOR,
    backgroundColor: ''
  })),
  template: 'CLASSIC',
  tickerLabel: '',
  ctaSubtext: '',
  tickerTextSize: 'MEDIUM',
  tickerBlockWeights: null,
  headingTextColor: DEFAULT_HEADING_TEXT_COLOR,
  headingBackground: DEFAULT_HEADING_BG,
  optionsTextColor: DEFAULT_OPTIONS_TEXT_COLOR,
  optionsBackground: DEFAULT_OPTIONS_BG,
  callToActionTextColor: DEFAULT_CTA_TEXT_COLOR,
  callToActionBackground: DEFAULT_CTA_BG,
  alternatingOptionBackground: DEFAULT_OPTIONS_BG,
  backgroundImage: '',
  backgroundImageFit: 'COVER',
  cornerRadius: DEFAULT_CORNER_RADIUS,
  borderWidth: DEFAULT_BORDER_WIDTH,
  borderColor: DEFAULT_BORDER_COLOR,
  winnerOutlineColor: DEFAULT_WINNER_OUTLINE_COLOR,
  fontKey: 'DEFAULT'
})

const PollPreviewBody = ({ draft, existing, readOnly, elementHeight }: {
  draft: PollDraft
  existing: PollSummary | null
  readOnly: boolean
  elementHeight?: number
}): JSX.Element => {
  const stageWidth = draft.template === 'TICKER' ? POLL.ticker.width : POLL.width
  if (readOnly && existing !== null) {
    return (
      <div>
        <div className='mb-1 text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent'>
          Last state
        </div>
        <ScaledStage mode='preview' showCheckerboard elementWidth={stageWidth} elementHeight={elementHeight}>
          <PollPreview draft={draft} live={pollLiveFromSummary(existing)} layoutWidth={stageWidth} />
        </ScaledStage>
      </div>
    )
  }
  return (
    <div className='space-y-3'>
      <div>
        <div className='mb-1 text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent'>
          Active (live voting)
        </div>
        <ScaledStage mode='preview' showCheckerboard elementWidth={stageWidth} elementHeight={elementHeight}>
          <PollPreview draft={draft} layoutWidth={stageWidth} />
        </ScaledStage>
      </div>
      <div>
        <div className='mb-1 text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent'>
          Results final (poll closed)
        </div>
        <ScaledStage mode='preview' showCheckerboard elementWidth={stageWidth} elementHeight={elementHeight}>
          <PollPreview draft={draft} live={sampleClosedPollLive(draft.options.length)} layoutWidth={stageWidth} />
        </ScaledStage>
      </div>
    </div>
  )
}

export const PollForm = ({
  event,
  existing,
  onSaved,
  onCancel
}: {
  event: EventDetail
  existing: PollSummary | null
  onSaved: (poll: PollSummary) => void
  onCancel: () => void
}): JSX.Element => {
  const { auth0Token } = useAuth()
  const { account } = useEntitlements()
  const [draft, setDraft] = useState<PollDraft>(() =>
    existing !== null ? draftFromPoll(existing) : emptyDraft()
  )
  const [broadcasterUserIds, setBroadcasterUserIds] = useState<string[]>(() =>
    existing !== null ? existing.broadcasterUserIds : event.channels.map((c) => c.broadcasterUserId)
  )
  const [dedupe, setDedupe] = useState<boolean>(() =>
    existing !== null ? existing.dedupe : true
  )
  const youtubeInvited = account?.youtubeChatInvited === true
  const showYouTubeOptIn = youtubeInvited || eventHasYouTubeSource(event)
  const [youtubeVotesEnabled, setYoutubeVotesEnabled] = useState<boolean>(() =>
    existing !== null ? existing.youtubeVotesEnabled === true : false
  )
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedNote, setSavedNote] = useState(false)
  useEffect(() => {
    setSavedNote(false)
  }, [draft, broadcasterUserIds, dedupe, youtubeVotesEnabled])

  const isEdit = existing !== null
  const readOnly = event.status === 'ENDED'
  const canAddOption = draft.options.length < (draft.template === 'TICKER' ? 4 : 6)
  const canRemoveOption = draft.options.length > 2

  const [showingInLiveStream, setShowingInLiveStream] = useState(false)
  const existingPollId = existing?.pollId
  useEffect(() => {
    if (existingPollId === undefined || event.status !== 'ACTIVE' || auth0Token === undefined) return
    let cancelled = false
    const check = async (): Promise<void> => {
      try {
        const kinds = ['DESKTOP', 'MOBILE']
        const results = await Promise.all(kinds.map(async (kind) =>
          await graphqlRequest<{ overlay: EventOverlay | null }>(
            getOverlayQuery, { eventId: event.eventId, kind }, auth0Token
          )
        ))
        const showing = results.some(({ overlay }) =>
          (overlay?.liveScene?.elements ?? []).some((el) =>
            el.elementKind === 'POLL' && el.elementId === existingPollId
          )
        )
        if (!cancelled) setShowingInLiveStream(showing)
      } catch {
      }
    }
    void check()
    return () => { cancelled = true }
  }, [existingPollId, event.status, event.eventId, auth0Token])
  const templateLockTitle = existing?.status === 'ACTIVE'
    ? 'Stop the poll before changing its template.'
    : showingInLiveStream
      ? 'This poll is showing in the live stream. Take it off air to change its template.'
      : undefined
  const templateLocked = isEdit && templateLockTitle !== undefined
  const tickerCapacityLocked = draft.options.length > 4
  const tickerLockTitle = (templateLocked ? templateLockTitle : undefined) ??
    (tickerCapacityLocked
      ? 'A ticker poll holds 2 to 4 options. Remove options to switch.'
      : undefined)
  const tickerLocked = templateLocked || tickerCapacityLocked

  const sizeDefaults = pollTextSizeDefaults(draft.template, draft.tickerTextSize)
  const setSize = (key: PollSizeKey) => (next: number | null): void => {
    setDraft((d) => ({ ...d, [key]: next }))
  }
  const [previewRef, previewFit] = usePreviewFit('poll-preview')
  const anySizeSet = POLL_SIZE_KEYS.some((k) => draft[k] != null)
  const previewHeight = anySizeSet && previewFit.frameHeight > 720
    ? Math.ceil(previewFit.frameHeight)
    : undefined
  const neededBarHeight = Math.ceil(Math.round(previewFit.rootHeight * 100) / 100)
  const barHeightHint = draft.template === 'TICKER' && draft.tickerHeight != null && neededBarHeight > draft.tickerHeight
    ? `Your text needs ${neededBarHeight} px, so the bar is ${neededBarHeight} px tall.`
    : null

  const updateOption = (idx: number, patch: Partial<PollDraft['options'][number]>): void => {
    setDraft((d) => ({
      ...d,
      options: d.options.map((o, i) => i === idx ? { ...o, ...patch } : o)
    }))
  }

  const addOption = (): void => {
    if (!canAddOption) return
    const letter = String.fromCharCode(65 + draft.options.length)
    setDraft((d) => ({
      ...d,
      options: [
        ...d.options,
        {
          displayText: `Option ${letter}`,
          command: `#${letter}`,
          textColor: DEFAULT_OPTION_TEXT_COLOR,
          progressBarColor: DEFAULT_OPTION_PROGRESS_COLOR,
          backgroundColor: ''
        }
      ],
      tickerBlockWeights: d.tickerBlockWeights != null
        ? appendTickerOptionWeight(d.tickerBlockWeights)
        : null
    }))
  }

  const removeOption = (idx: number): void => {
    if (!canRemoveOption) return
    setDraft((d) => ({
      ...d,
      options: d.options.filter((_, i) => i !== idx),
      tickerBlockWeights: d.tickerBlockWeights != null
        ? removeTickerOptionWeight(d.tickerBlockWeights, idx)
        : null
    }))
  }

  const toggleChannel = (broadcasterUserId: string): void => {
    setBroadcasterUserIds((prev) => prev.includes(broadcasterUserId)
      ? prev.filter((id) => id !== broadcasterUserId)
      : [...prev, broadcasterUserId])
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault()
    if (auth0Token === undefined || submitting) return
    if (draft.question.trim().length === 0) {
      setError('Question is required.')
      return
    }
    const cmds = draft.options.map((o) => o.command.toLowerCase().trim())
    if (new Set(cmds).size !== cmds.length) {
      setError('Option commands must be unique.')
      return
    }
    if (cmds.some((c) => c.length === 0)) {
      setError('Every option needs a command.')
      return
    }

    setSubmitting(true)
    setError(null)
    try {
      const optionsInput: PollOptionInput[] = draft.options.map((o) => ({
        displayText: o.displayText,
        command: o.command.trim(),
        textColor: o.textColor,
        progressBarColor: o.progressBarColor,
        backgroundColor: o.backgroundColor.trim().length > 0
          ? o.backgroundColor
          : null
      }))
      const sharedVars = {
        name: draft.name,
        broadcasterUserIds,
        question: draft.question,
        options: optionsInput,
        dedupe,
        youtubeVotesEnabled,
        template: draft.template,
        tickerLabel: draft.tickerLabel.trim().length > 0
          ? draft.tickerLabel
          : null,
        ctaSubtext: draft.ctaSubtext.trim().length > 0
          ? draft.ctaSubtext
          : null,
        tickerTextSize: draft.tickerTextSize,
        ...(draft.template === 'TICKER'
          ? { tickerBlockWeights: draft.tickerBlockWeights ?? null }
          : {}),
        callToActionText: draft.callToActionText.trim().length > 0
          ? draft.callToActionText
          : null,
        headingTextColor: draft.headingTextColor,
        optionsTextColor: draft.optionsTextColor,
        callToActionTextColor: draft.callToActionTextColor,
        headingBackground: draft.headingBackground,
        optionsBackground: draft.optionsBackground,
        callToActionBackground: draft.callToActionBackground,
        alternatingOptionBackground: draft.alternatingOptionBackground === draft.optionsBackground
          ? null
          : draft.alternatingOptionBackground,
        backgroundImage: draft.backgroundImage.trim().length > 0
          ? draft.backgroundImage
          : null,
        backgroundImageFit: draft.backgroundImage.trim().length > 0
          ? draft.backgroundImageFit
          : null,
        cornerRadius: draft.cornerRadius,
        borderWidth: draft.borderWidth,
        borderColor: draft.borderColor,
        winnerOutlineColor: draft.winnerOutlineColor,
        fontKey: draft.fontKey === 'DEFAULT' ? null : draft.fontKey
      }
      const textSizeVars = {
        questionFontSize: draft.questionFontSize ?? null,
        tickerLabelFontSize: draft.tickerLabelFontSize ?? null,
        displayTextFontSize: draft.displayTextFontSize ?? null,
        commandFontSize: draft.commandFontSize ?? null,
        numberFontSize: draft.numberFontSize ?? null,
        callToActionFontSize: draft.callToActionFontSize ?? null,
        closedTextFontSize: draft.closedTextFontSize ?? null,
        ...(draft.template === 'TICKER' ? { tickerHeight: draft.tickerHeight ?? null } : {})
      }
      if (isEdit) {
        const { poll } = await graphqlRequest<{ poll: PollSummary }>(
          draft.template === 'TICKER' ? updatePollMutation : updatePollClassicMutation,
          { pollId: existing.pollId, ...sharedVars, ...textSizeVars },
          auth0Token
        )
        setSavedNote(true)
        onSaved(poll)
      } else {
        const { poll } = await graphqlRequest<{ poll: PollSummary }>(
          createPollMutation,
          { eventId: event.eventId, ...sharedVars, ...textSizeVars },
          auth0Token
        )
        setSavedNote(true)
        onSaved(poll)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save poll')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form
      onSubmit={(e) => { void handleSubmit(e) }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
          e.preventDefault()
        }
      }}
      className='mt-2 grid grid-cols-1 gap-3 px-4 pb-4 lg:grid-cols-3'
    >
      <div className='space-y-1.5 lg:col-span-2'>
        <fieldset disabled={readOnly} className='contents space-y-1.5'>
          <SectionCard
            title={readOnly ? 'View poll' : isEdit ? 'Edit poll' : 'New poll'}
            action={
            readOnly
              ? (
                <span className='inline-flex items-center gap-1.5 rounded-full bg-dm-bg-accent/50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent ring-1 ring-inset ring-white/10'>
                  <EyeIcon className='size-3' aria-hidden='true' />
                  Read-only
                </span>
                )
              : (
                <button
                  type='button'
                  onClick={onCancel}
                  className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary'
                >
                  <ArrowLeftIcon className='size-3.5' aria-hidden='true' />
                  Back to polls
                </button>
                )
          }
          >
            <div className='mb-4'>
              <FieldLabel>Template</FieldLabel>
              <div role='radiogroup' aria-label='Poll template' className='grid grid-cols-2 gap-3'>
                <button
                  type='button'
                  role='radio'
                  aria-checked={draft.template === 'CLASSIC'}
                  disabled={templateLocked}
                  title={templateLockTitle}
                  onClick={() => setDraft((d) => ({ ...d, template: 'CLASSIC' }))}
                  className={classNames(
                    'rounded-lg border p-3 disabled:cursor-not-allowed disabled:opacity-60',
                    draft.template === 'CLASSIC'
                      ? 'border-brand ring-1 ring-brand bg-dm-bg-accent/40'
                      : 'border-white/10 bg-dm-bg-accent/20 hover:border-white/25'
                  )}
                >
                  <span aria-hidden='true' className='mx-auto flex h-16 w-24 flex-col overflow-hidden rounded-md border border-white/10'>
                    <span className='h-4 shrink-0 bg-indigo-800' />
                    <span className='flex flex-1 flex-col justify-center gap-0.5 bg-blue-700 px-1.5'>
                      <span className='block h-1 w-3/4 rounded-full bg-cyan-300/80' />
                    </span>
                    <span className='flex flex-1 flex-col justify-center gap-0.5 bg-blue-800 px-1.5'>
                      <span className='block h-1 w-1/2 rounded-full bg-cyan-300/80' />
                    </span>
                    <span className='h-3 shrink-0 bg-slate-900' />
                  </span>
                  <span className='mt-2 block text-center text-xs font-medium text-dm-text-primary'>
                    Classic
                  </span>
                </button>
                <button
                  type='button'
                  role='radio'
                  aria-checked={draft.template === 'TICKER'}
                  disabled={tickerLocked}
                  title={tickerLockTitle}
                  onClick={() => setDraft((d) => ({ ...d, template: 'TICKER' }))}
                  className={classNames(
                    'rounded-lg border p-3 disabled:cursor-not-allowed disabled:opacity-60',
                    draft.template === 'TICKER'
                      ? 'border-brand ring-1 ring-brand bg-dm-bg-accent/40'
                      : 'border-white/10 bg-dm-bg-accent/20 hover:border-white/25'
                  )}
                >
                  <span aria-hidden='true' className='mx-auto flex h-16 w-full max-w-[160px] flex-col justify-end'>
                    <span className='flex h-5 w-full overflow-hidden rounded-sm border border-white/10'>
                      <span className='w-1/4 shrink-0 bg-indigo-800' />
                      <span className='flex flex-1 flex-col justify-end bg-orange-700 px-0.5 pb-0.5'>
                        <span className='block h-0.5 w-full rounded-full bg-white/70' />
                      </span>
                      <span className='flex flex-1 flex-col justify-end bg-purple-700 px-0.5 pb-0.5'>
                        <span className='block h-0.5 w-2/3 rounded-full bg-white/70' />
                      </span>
                      <span className='flex flex-1 flex-col justify-end bg-rose-800 px-0.5 pb-0.5'>
                        <span className='block h-0.5 w-1/3 rounded-full bg-white/70' />
                      </span>
                      <span className='w-1/5 shrink-0 bg-slate-900' />
                    </span>
                  </span>
                  <span className='mt-2 block text-center text-xs font-medium text-dm-text-primary'>
                    Ticker bar
                  </span>
                </button>
              </div>
            </div>
            <label className='mb-4 block'>
              <FieldLabel>Name</FieldLabel>
              <input
                value={draft.name}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                placeholder='Short label for this poll (shown in the list)'
                className='w-full rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
              />
              <p className='mt-1 text-[11px] text-dm-text-accent'>
                Displayed in list. Defaults to the question text when empty.
              </p>
            </label>
            <label className='mb-4 block'>
              <FieldLabel>Question</FieldLabel>
              <input
                value={draft.question}
                onChange={(e) => setDraft((d) => ({ ...d, question: e.target.value }))}
                placeholder='Which bonus challenge should be sent to Team 1?'
                required
                className='w-full rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
              />
            </label>
            {draft.template === 'TICKER' && (
              <label className='mb-4 block'>
                <FieldLabel>Ticker label (optional)</FieldLabel>
                <input
                  value={draft.tickerLabel}
                  onChange={(e) => setDraft((d) => ({ ...d, tickerLabel: e.target.value }))}
                  maxLength={80}
                  placeholder='LIVE PREDICTION'
                  className='w-full rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
                />
              </label>
            )}
            <label className='mb-4 block'>
              <FieldLabel>Call-to-action text</FieldLabel>
              <input
                value={draft.callToActionText}
                onChange={(e) => setDraft((d) => ({ ...d, callToActionText: e.target.value }))}
                placeholder={DEFAULT_CTA_TEXT}
                className='w-full rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
              />
              <p className='mt-1 text-[11px] text-dm-text-accent'>
                Leave empty to hide the bottom strip on the overlay.
              </p>
            </label>
            {draft.template === 'TICKER' && (
              <label className='mb-4 block'>
                <FieldLabel>Call-to-action sub-line (optional)</FieldLabel>
                <input
                  value={draft.ctaSubtext}
                  onChange={(e) => setDraft((d) => ({ ...d, ctaSubtext: e.target.value }))}
                  maxLength={200}
                  placeholder='Type 1, 2 or 3'
                  className='w-full rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
                />
              </label>
            )}

            <div className='mb-4'>
              <FieldLabel>
                <span className='flex items-center gap-1.5'>
                  <ProviderBrandIcon src={TWITCH_BRAND_ICON_SRC} size='sm' />
                  Twitch channels
                </span>
              </FieldLabel>
              {event.channels.length === 0
                ? (
                  <p className='rounded-md border border-dashed border-white/10 p-3 text-xs text-dm-text-secondary'>
                    No channels bound to this stream yet. Add one on the Integrations page.
                  </p>
                  )
                : (
                  <ul className='space-y-1.5'>
                    {event.channels.map((c: ChannelBinding) => (
                      <li key={c.broadcasterUserId}>
                        <label className='flex cursor-pointer items-center gap-2.5 rounded-md border border-white/5 bg-dm-bg-accent/30 px-3 py-2 text-sm hover:bg-dm-bg-accent/50'>
                          <input
                            type='checkbox'
                            checked={broadcasterUserIds.includes(c.broadcasterUserId)}
                            onChange={() => toggleChannel(c.broadcasterUserId)}
                            className='size-4 rounded border-white/20 bg-dm-bg-accent/60 text-brand focus:ring-brand'
                          />
                          <ProviderBrandIcon src={TWITCH_BRAND_ICON_SRC} size='sm' />
                          <span className='text-dm-text-primary'>{c.channelLogin}</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                  )}
              {showYouTubeOptIn && (
                <p className='mt-1.5 text-[11px] text-dm-text-accent' data-testid='twitch-channels-optional-hint'>
                  {TWITCH_CHANNELS_OPTIONAL_HINT}
                </p>
              )}
            </div>

            <label className='flex items-center gap-2 text-sm text-dm-text-primary'>
              <input
                type='checkbox'
                checked={dedupe}
                onChange={(e) => setDedupe(e.target.checked)}
                className='size-4 rounded border-white/20 bg-dm-bg-accent/60 text-brand focus:ring-brand'
              />
              One vote per user in chat (dedupe)
            </label>
            {showYouTubeOptIn && (
              <label className='mt-3 flex items-center gap-2 text-sm text-dm-text-primary'>
                <input
                  type='checkbox'
                  checked={youtubeVotesEnabled}
                  onChange={(e) => setYoutubeVotesEnabled(e.target.checked)}
                  className='size-4 rounded border-white/20 bg-dm-bg-accent/60 text-brand focus:ring-brand'
                />
                Count YouTube votes (this stream&apos;s YouTube sources)
              </label>
            )}
          </SectionCard>
          <SectionCard
            title='Options'
            action={
              <button
                type='button'
                onClick={addOption}
                disabled={!canAddOption}
                title={!canAddOption && draft.template === 'TICKER'
                  ? 'A ticker poll holds 2 to 4 options.'
                  : undefined}
                className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
              >
                <PlusIcon className='size-3.5' aria-hidden='true' />
                Add option
              </button>
          }
          >
            <ul className='space-y-3'>
              {draft.options.map((opt, i) => (
                <li
                  key={i}
                  className='rounded-lg border border-white/5 bg-dm-bg-accent/30 p-3'
                >
                  <div className='mb-2 flex items-center justify-between'>
                    <span className='text-xs font-semibold uppercase tracking-wider text-dm-text-accent'>
                      Option {i + 1}
                    </span>
                    <button
                      type='button'
                      onClick={() => removeOption(i)}
                      disabled={!canRemoveOption}
                      title='Remove this option'
                      className='inline-flex items-center gap-1 rounded-md border border-white/10 px-2 py-0.5 text-[11px] text-dm-text-secondary hover:border-red-500/40 hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-60'
                    >
                      <TrashIcon className='size-3' aria-hidden='true' />
                      Remove
                    </button>
                  </div>
                  <div className='grid grid-cols-2 gap-3'>
                    <label className='block'>
                      <FieldLabel>Display text</FieldLabel>
                      <input
                        value={opt.displayText}
                        onChange={(e) => updateOption(i, { displayText: e.target.value })}
                        required
                        className='w-full rounded-md border border-white/10 bg-dm-bg-secondary px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
                      />
                    </label>
                    <label className='block'>
                      <FieldLabel>Chat command</FieldLabel>
                      <input
                        value={opt.command}
                        onChange={(e) => updateOption(i, { command: e.target.value })}
                        required
                        className='w-full rounded-md border border-white/10 bg-dm-bg-secondary px-3 py-2 font-mono text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
                      />
                    </label>
                    <ColorInput
                      label='Text color'
                      value={opt.textColor}
                      onChange={(v) => updateOption(i, { textColor: v })}
                    />
                    <ColorInput
                      label='Progress bar color'
                      value={opt.progressBarColor}
                      onChange={(v) => updateOption(i, { progressBarColor: v })}
                    />
                    <div className='col-span-2'>
                      <FieldLabel>Row background (optional)</FieldLabel>
                      <div className='flex items-start gap-2'>
                        <div className='min-w-0 flex-1'>
                          <ColorInput
                            inline
                            value={opt.backgroundColor}
                            onChange={(v) => updateOption(i, { backgroundColor: v })}
                          />
                        </div>
                        <button
                          type='button'
                          onClick={() => updateOption(i, { backgroundColor: '' })}
                          disabled={opt.backgroundColor.trim().length === 0}
                          title='Use the shared row backgrounds for this option'
                          className='rounded-md border border-white/10 px-2.5 py-2 text-[11px] text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-50'
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </SectionCard>
          <SectionCard title='Font'>
            <label className='block'>
              <FieldLabel>Font</FieldLabel>
              <FontSelect
                value={draft.fontKey}
                onChange={(next) => setDraft((d) => ({ ...d, fontKey: next as ChatFontKey }))}
                defaultLabel='Default'
                className='w-full rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
              />
            </label>
          </SectionCard>
          <SectionCard title='Text sizes'>
            <p className='mb-3 text-[11px] text-dm-text-accent'>
              Pixel sizes on the 1920 x 1080 stream canvas at 100% placement.
              Leave a box empty to use the default shown in it. Resizing the
              poll in the Overlay Editor scales these too.
            </p>
            <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
              <TextSizeInput
                label='Question'
                testId='size-question'
                value={draft.questionFontSize ?? null}
                defaultPx={sizeDefaults.question}
                min={TEXT_SIZE_MIN_PX}
                max={TEXT_SIZE_MAX_PX}
                onChange={setSize('questionFontSize')}
              />
              {draft.template === 'TICKER' && (
                <TextSizeInput
                  label='Ticker label'
                  testId='size-ticker-label'
                  value={draft.tickerLabelFontSize ?? null}
                  defaultPx={sizeDefaults.tickerLabel}
                  min={TEXT_SIZE_MIN_PX}
                  max={TEXT_SIZE_MAX_PX}
                  onChange={setSize('tickerLabelFontSize')}
                />
              )}
              <TextSizeInput
                label='Display text'
                testId='size-display-text'
                value={draft.displayTextFontSize ?? null}
                defaultPx={sizeDefaults.displayText}
                min={TEXT_SIZE_MIN_PX}
                max={TEXT_SIZE_MAX_PX}
                onChange={setSize('displayTextFontSize')}
                help={draft.template === 'TICKER'
                  ? 'Option titles always wrap to more lines instead of being cut. A smaller size keeps the bar slim.'
                  : undefined}
              />
              <TextSizeInput
                label='Chat command'
                testId='size-command'
                value={draft.commandFontSize ?? null}
                defaultPx={sizeDefaults.command}
                min={TEXT_SIZE_MIN_PX}
                max={TEXT_SIZE_MAX_PX}
                onChange={setSize('commandFontSize')}
              />
              <TextSizeInput
                label='Number (percent or votes)'
                testId='size-number'
                value={draft.numberFontSize ?? null}
                defaultPx={sizeDefaults.number}
                min={TEXT_SIZE_MIN_PX}
                max={TEXT_SIZE_MAX_PX}
                onChange={setSize('numberFontSize')}
              />
              <TextSizeInput
                label='Call to action'
                testId='size-call-to-action'
                value={draft.callToActionFontSize ?? null}
                defaultPx={sizeDefaults.callToAction}
                min={TEXT_SIZE_MIN_PX}
                max={TEXT_SIZE_MAX_PX}
                onChange={setSize('callToActionFontSize')}
                help={draft.template === 'TICKER' ? 'The sub-line under it scales with it.' : undefined}
              />
              <TextSizeInput
                label='Closed label'
                testId='size-closed-text'
                value={draft.closedTextFontSize ?? null}
                defaultPx={sizeDefaults.closedText}
                min={TEXT_SIZE_MIN_PX}
                max={TEXT_SIZE_MAX_PX}
                onChange={setSize('closedTextFontSize')}
                help='The "Poll closed • Results final" label after you stop a poll.'
              />
            </div>
            {draft.template === 'TICKER' && (
              <div className='mt-4'>
                <TextSizeInput
                  label='Ticker bar height'
                  testId='size-ticker-height'
                  value={draft.tickerHeight ?? null}
                  defaultPx={null}
                  min={TICKER_HEIGHT_MIN_PX}
                  max={TICKER_HEIGHT_MAX_PX}
                  onChange={setSize('tickerHeight')}
                  help='Leave empty for the automatic height (at least 116 px). The bar always grows to fit its text.'
                />
                {barHeightHint != null && (
                  <p data-testid='ticker-height-hint' className='mt-1 text-[11px] text-amber-300'>
                    {barHeightHint}
                  </p>
                )}
              </div>
            )}
            {previewFit.overflows && (
              <p data-testid='text-fit-hint' className='mt-3 text-[11px] text-amber-300'>
                Some text is wider than its box at these sizes and gets cut off. Try a smaller size.
              </p>
            )}
          </SectionCard>

          <SectionCard title='Heading style'>
            <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
              <ColorInput
                label='Heading text color'
                value={draft.headingTextColor}
                onChange={(v) => setDraft((d) => ({ ...d, headingTextColor: v }))}
              />
              <BackgroundInput
                label='Heading background'
                value={draft.headingBackground}
                onChange={(v) => setDraft((d) => ({ ...d, headingBackground: v }))}
              />
            </div>
          </SectionCard>

          <SectionCard title='Options section style'>
            <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
              <BackgroundInput
                label='Odd row background (1st, 3rd, 5th…)'
                value={draft.optionsBackground}
                onChange={(v) => setDraft((d) => ({ ...d, optionsBackground: v }))}
              />
              <BackgroundInput
                label='Even row background (2nd, 4th, 6th…)'
                value={draft.alternatingOptionBackground}
                onChange={(v) => setDraft((d) => ({ ...d, alternatingOptionBackground: v }))}
              />
            </div>
          </SectionCard>

          <SectionCard title='Container shape'>
            <label className='block'>
              <FieldLabel>Corner radius (px)</FieldLabel>
              <div className='flex items-center gap-2'>
                <input
                  type='range'
                  min={MIN_CORNER_RADIUS}
                  max={MAX_CORNER_RADIUS}
                  value={draft.cornerRadius}
                  onChange={(e) => setDraft((d) => ({ ...d, cornerRadius: Number.parseInt(e.target.value, 10) }))}
                  className='flex-1 accent-brand'
                />
                <NumberInput
                  value={draft.cornerRadius}
                  min={MIN_CORNER_RADIUS}
                  max={MAX_CORNER_RADIUS}
                  onCommit={(n) => setDraft((d) => ({ ...d, cornerRadius: n }))}
                  className='w-20 rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-xs text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
                />
              </div>
            </label>
            <label className='mt-4 block'>
              <FieldLabel>Border width (px)</FieldLabel>
              <NumberInput
                value={draft.borderWidth}
                min={MIN_BORDER_WIDTH}
                max={MAX_BORDER_WIDTH}
                onCommit={(n) => setDraft((d) => ({ ...d, borderWidth: n }))}
                className='w-24 rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-xs text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
              />
            </label>
            <div className='mt-4'>
              <ColorInput
                label='Border color'
                value={draft.borderColor}
                onChange={(v) => setDraft((d) => ({ ...d, borderColor: v }))}
              />
            </div>
            <div className='mt-4'>
              <ColorInput
                label='Winner outline color'
                value={draft.winnerOutlineColor}
                onChange={(v) => setDraft((d) => ({ ...d, winnerOutlineColor: v }))}
              />
            </div>
          </SectionCard>

          <SectionCard title='Overlay background image'>
            <BackgroundImageInput
              url={draft.backgroundImage}
              fit={draft.backgroundImageFit}
              onChangeUrl={(v) => setDraft((d) => ({ ...d, backgroundImage: v }))}
              onChangeFit={(v) => setDraft((d) => ({ ...d, backgroundImageFit: v }))}
              kind='POLL_BACKGROUND'
              label='Background image (optional)'
            />
          </SectionCard>

          <SectionCard title='Call-to-action style'>
            <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
              <ColorInput
                label='Call-to-action text color'
                value={draft.callToActionTextColor}
                onChange={(v) => setDraft((d) => ({ ...d, callToActionTextColor: v }))}
              />
              <BackgroundInput
                label='Call-to-action background'
                value={draft.callToActionBackground}
                onChange={(v) => setDraft((d) => ({ ...d, callToActionBackground: v }))}
              />
            </div>
          </SectionCard>

        </fieldset>
      </div>
      <div className='lg:col-span-1'>
        <div className='sticky top-4 space-y-3'>
          {readOnly
            ? (
              <>
                <div className='rounded-md border border-white/10 bg-dm-bg-accent/30 px-3 py-2 text-[11px] leading-relaxed text-dm-text-secondary'>
                  This stream has ended, so its polls are read-only. The preview below shows the poll&rsquo;s last state.
                </div>
                <button
                  type='button'
                  onClick={onCancel}
                  className='inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary'
                >
                  <ArrowLeftIcon className='size-3.5' aria-hidden='true' />
                  Back to polls
                </button>
              </>
              )
            : (
              <div className='flex items-center gap-2'>
                <button
                  type='submit'
                  disabled={submitting}
                  className='inline-flex flex-1 items-center justify-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
                >
                  {submitting && <LoadingSpinner />}
                  {isEdit ? 'Save changes' : 'Create poll'}
                </button>
                <button
                  type='button'
                  onClick={onCancel}
                  disabled={submitting}
                  className='inline-flex flex-1 items-center justify-center rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:opacity-60'
                >
                  Cancel
                </button>
              </div>
              )}
          {error !== null && <ErrorInline onDismiss={() => setError(null)}>{error}</ErrorInline>}
          {savedNote && <SavedNote testId='poll-saved' />}
          <h4 className='text-xs font-semibold uppercase tracking-wider text-dm-text-accent'>
            Preview
          </h4>
          <div ref={previewRef}>
            <PollPreviewBody draft={draft} existing={existing} readOnly={readOnly} elementHeight={previewHeight} />
          </div>
        </div>
      </div>
    </form>
  )
}

const pollPlatformTotals = (poll: PollSummary): { twitch: number, youtube: number } => ({
  twitch: sumCounts(poll.options.map((o) => o.voteCountByPlatform?.twitch ?? o.voteCount)),
  youtube: sumCounts(poll.options.map((o) => o.voteCountByPlatform?.youtube ?? 0))
})

const pollVoteFigure = (poll: PollSummary): string => {
  const { twitch, youtube } = pollPlatformTotals(poll)
  return youtube > 0 ? `Twitch ${twitch} · YouTube ${youtube}` : `${poll.totalVotes} votes`
}

const PollListRow = ({
  poll,
  onEdit,
  onDuplicate,
  onDelete,
  busyAction,
  readOnly
}: {
  poll: PollSummary
  onEdit: () => void
  onDuplicate: () => void
  onDelete: () => void
  busyAction: null | 'delete' | 'duplicate'
  readOnly: boolean
}): JSX.Element => {
  const isActive = poll.status === 'ACTIVE'
  return (
    <li
      data-testid={`poll-row-${poll.pollId}`}
      className='rounded-lg border border-white/5 bg-dm-bg-accent/30 px-4 py-3'
    >
      <div className='flex items-center justify-between gap-3'>
        <div className='flex min-w-0 items-center gap-3'>
          <span className={classNames(
            'shrink-0 rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ring-1 ring-inset',
            STATUS_PILL[poll.status]
          )}
          >
            {STATUS_LABEL[poll.status]}
          </span>
          <div className='flex min-w-0 flex-col leading-tight'>
            <span className='truncate text-sm font-medium text-dm-text-primary' title={poll.question}>
              {elementDisplayName(poll.name, poll.question)}
            </span>
            <span className='truncate text-[11px] text-dm-text-accent'>
              {poll.options.length} options · {pollVoteFigure(poll)}
              {poll.broadcasterUserIds.length > 1 ? ` · ${poll.broadcasterUserIds.length} channels` : ''}
            </span>
          </div>
        </div>
        <div className='flex shrink-0 items-center gap-1.5'>
          <button
            type='button'
            onClick={onEdit}
            title={readOnly ? 'View' : 'Edit'}
            disabled={busyAction !== null}
            className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
          >
            {readOnly
              ? <EyeIcon className='size-3.5' aria-hidden='true' />
              : <PencilSquareIcon className='size-3.5' aria-hidden='true' />}
            {readOnly ? 'View' : 'Edit'}
          </button>
          {!readOnly && (
            <button
              type='button'
              onClick={onDuplicate}
              title='Duplicate'
              disabled={busyAction !== null}
              className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
            >
              {busyAction === 'duplicate' ? <LoadingSpinner /> : <DocumentDuplicateIcon className='size-3.5' aria-hidden='true' />}
              Duplicate
            </button>
          )}
          <button
            type='button'
            onClick={onDelete}
            disabled={busyAction !== null || isActive}
            title={isActive ? 'Stop this poll first — voting controls are in the overlay editor' : 'Delete'}
            className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-red-500/40 hover:bg-red-500/10 hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-60'
          >
            {busyAction === 'delete' ? <LoadingSpinner /> : <TrashIcon className='size-3.5' aria-hidden='true' />}
            Delete
          </button>
        </div>
      </div>
    </li>
  )
}

type Mode =
  | { kind: 'list' }
  | { kind: 'create' }
  | { kind: 'edit', pollId: string }

export const PollsPage = (): JSX.Element => {
  const { auth0Token } = useAuth()
  const { event, setEvent, refetchEvent } = useEventDetail()
  const isEnded = event.status === 'ENDED'
  const [mode, setMode] = useState<Mode>({ kind: 'list' })
  const [rowBusy, setRowBusy] = useState<{ pollId: string, action: 'delete' | 'duplicate' } | null>(null)
  const [rowError, setRowError] = useState<string | null>(null)

  const editing = useMemo<PollSummary | null>(() => {
    if (mode.kind !== 'edit') return null
    return event.polls.find((p) => p.pollId === mode.pollId) ?? null
  }, [mode, event.polls])

  const upsertPoll = (poll: PollSummary): void => {
    setEvent((prev) => prev === null
      ? prev
      : ({
          ...prev,
          polls: prev.polls.some((p) => p.pollId === poll.pollId)
            ? prev.polls.map((p) => p.pollId === poll.pollId ? poll : p)
            : [...prev.polls, poll]
        }))
  }

  const removePoll = (pollId: string): void => {
    setEvent((prev) => prev === null
      ? prev
      : ({
          ...prev,
          polls: prev.polls.filter((p) => p.pollId !== pollId)
        }))
  }

  const handleDelete = async (poll: PollSummary): Promise<void> => {
    if (auth0Token === undefined || rowBusy !== null) return
    const totals = pollPlatformTotals(poll)
    const recorded = totals.youtube > 0
      ? `recorded votes (Twitch ${totals.twitch} · YouTube ${totals.youtube})`
      : `${poll.totalVotes} recorded vote${poll.totalVotes === 1 ? '' : 's'}`
    const ok = globalThis.confirm(
      poll.totalVotes > 0 || totals.twitch > 0 || totals.youtube > 0
        ? `Delete poll "${poll.question}"?\n\nWARNING: this poll has ${recorded}. Deleting it permanently destroys that vote data. It cannot be recovered. (Reset also destroys vote records; to keep results, create a new poll instead.)\n\nDelete and destroy the votes?`
        : `Delete poll "${poll.question}"? This cannot be undone.`
    )
    if (!ok) return
    setRowBusy({ pollId: poll.pollId, action: 'delete' })
    setRowError(null)
    try {
      const { result } = await graphqlRequest<{ result: DeletionResult }>(
        deletePollMutation, { pollId: poll.pollId }, auth0Token
      )
      if (result.deleted) removePoll(poll.pollId)
    } catch (err) {
      setRowError(err instanceof Error ? err.message : 'Failed to delete poll')
    } finally {
      setRowBusy(null)
    }
  }

  const handleDuplicate = async (poll: PollSummary): Promise<void> => {
    if (auth0Token === undefined || rowBusy !== null) return
    setRowBusy({ pollId: poll.pollId, action: 'duplicate' })
    setRowError(null)
    try {
      const { poll: created } = await graphqlRequest<{ poll: PollSummary }>(
        duplicatePollMutation, { pollId: poll.pollId }, auth0Token
      )
      upsertPoll(created)
    } catch (err) {
      setRowError(err instanceof Error ? err.message : 'Failed to duplicate poll')
    } finally {
      setRowBusy(null)
    }
  }

  if (mode.kind === 'create') {
    return (
      <PollForm
        event={event}
        existing={null}
        onSaved={(poll) => { upsertPoll(poll); setMode({ kind: 'edit', pollId: poll.pollId }) }}
        onCancel={() => setMode({ kind: 'list' })}
      />
    )
  }

  if (mode.kind === 'edit') {
    if (editing === null) {
      return (
        <div className='mt-4 px-4 pb-4'>
          <ErrorInline>That poll no longer exists. Returning to the list…</ErrorInline>
          <button onClick={() => setMode({ kind: 'list' })} className='mt-3 rounded-md border border-white/10 px-3 py-1.5 text-xs text-dm-text-secondary hover:text-dm-text-primary'>
            Back to polls
          </button>
        </div>
      )
    }
    return (
      <PollForm
        event={event}
        existing={editing}
        onSaved={upsertPoll}
        onCancel={() => setMode({ kind: 'list' })}
      />
    )
  }

  return (
    <div className='mt-2 flex flex-col gap-1.5 px-4 pb-4'>
      <SectionCard
        title='Polls'
        action={
          <div className='flex items-center gap-2'>
            <button
              type='button'
              onClick={() => { void refetchEvent() }}
              title='Refresh'
              className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1.5 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary'
            >
              <ArrowPathIcon className='size-3.5' aria-hidden='true' />
              Refresh
            </button>
            {!isEnded && (
              <button
                type='button'
                data-testid='add-poll-button'
                onClick={() => setMode({ kind: 'create' })}
                className='inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80'
              >
                <PlusIcon className='size-3.5' aria-hidden='true' />
                Add new poll
              </button>
            )}
          </div>
        }
      >
        {rowError !== null && (
          <div className='mb-3'>
            <ErrorInline onDismiss={() => setRowError(null)}>{rowError}</ErrorInline>
          </div>
        )}

        {event.polls.length === 0
          ? (
            <div className='flex flex-col items-center gap-3 rounded-lg border border-dashed border-white/10 px-4 py-10 text-center text-sm text-dm-text-secondary'>
              <ChartBarIcon className='size-8 text-dm-text-accent' aria-hidden='true' />
              <p>{isEnded ? 'This stream ended with no polls.' : 'No polls yet for this stream.'}</p>
              {!isEnded && (
                <button
                  type='button'
                  onClick={() => setMode({ kind: 'create' })}
                  className='inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80'
                >
                  <PlusIcon className='size-3.5' aria-hidden='true' />
                  Create the first one
                </button>
              )}
            </div>
            )
          : (
            <ul className='space-y-2' data-testid='polls-list'>
              {sortByName(event.polls, (p) => elementDisplayName(p.name, p.question), (p) => p.pollId).map((poll: PollSummary) => (
                <PollListRow
                  key={poll.pollId}
                  poll={poll}
                  onEdit={() => setMode({ kind: 'edit', pollId: poll.pollId })}
                  onDuplicate={() => { void handleDuplicate(poll) }}
                  onDelete={() => { void handleDelete(poll) }}
                  busyAction={rowBusy?.pollId === poll.pollId ? rowBusy.action : null}
                  readOnly={isEnded}
                />
              ))}
            </ul>
            )}
      </SectionCard>
    </div>
  )
}
